/**
 * DocumentViewerModal — Content component for reviewing uploaded documents.
 *
 * Renders inside GlobalModal. Streams decrypted document binary from the
 * backend (no SAS URL exposed). Shows the document alongside a findings sidebar.
 *
 * Supports three viewing modes:
 *  - Original: the uploaded document as-is
 *  - Revised (Clean): accepted/modified finding decisions applied, no markup
 *  - Revised (Redline): strikethrough original + highlighted replacement text
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import LogoSpinner from '../shared/LogoSpinner';
import {
  PiFileTextLight,
  PiDownloadSimpleLight,
  PiArrowsClockwiseLight,
} from 'react-icons/pi';

import {
  fetchDocumentContent,
  streamReviewDocument,
  streamRevisedDocument,
} from '../../store/actions/Review/ReviewActions';
import { FindingOverlay } from '../../types/review';
import { extractHtmlFromDocx } from '../../utils/docxHandler';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, documentViewerTourSteps } from '../shared/FeatureTour';

// ─── Severity dot colours ───────────────────────────────────────────────────

const severityDotColor: Record<string, string> = {
  critical: 'bg-red-500',
  major: 'bg-orange-400',
  minor: 'bg-yellow-400',
  info: 'bg-blue-400',
};

const decisionIcons: Record<string, { icon: string; color: string; label: string }> = {
  accepted: { icon: '✓', color: 'text-emerald-600 dark:text-emerald-400', label: 'Accepted' },
  rejected: { icon: '✕', color: 'text-red-500 dark:text-red-400', label: 'Rejected' },
  modified: { icon: '✎', color: 'text-amber-600 dark:text-amber-400', label: 'Modified' },
  escalated: { icon: '↗', color: 'text-purple-600 dark:text-purple-400', label: 'Escalated' },
};

const formatLabel = (text: string): string =>
  text
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

// ─── View mode types ────────────────────────────────────────────────────────

type ViewSource = 'original' | 'revised';
type RevisionStyle = 'clean' | 'redline';

// ─── Props ──────────────────────────────────────────────────────────────────

interface DocumentViewerModalProps {
  sessionUuid: string;
  documentId: number;
  documentName: string;
}

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  sessionUuid,
  documentId,
  documentName,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const documentContent = useSelector((s: RootState) => s.review.documentContent);
  const isLoading = useSelector((s: RootState) => s.review.isLoadingDocument);

  const [showFindings, setShowFindings] = useState(true);

  // ── Original document state ──
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [streamedContentType, setStreamedContentType] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);

  // ── Viewing mode state ──
  const [viewSource, setViewSource] = useState<ViewSource>('original');
  const [revisionStyle, setRevisionStyle] = useState<RevisionStyle>('clean');

  // ── Revised document state ──
  const [revisedBlobUrl, setRevisedBlobUrl] = useState<string | null>(null);
  const [revisedContentType, setRevisedContentType] = useState<string>('');
  const [revisedDocxHtml, setRevisedDocxHtml] = useState<string | null>(null);
  const [isStreamingRevised, setIsStreamingRevised] = useState(false);
  const [revisedError, setRevisedError] = useState(false);
  const [noRevisionAvailable, setNoRevisionAvailable] = useState(false);

  // Track which mode was last fetched to avoid re-fetching
  const lastFetchedStyle = useRef<RevisionStyle | null>(null);

  // Ref to the document iframe for scroll-to-finding
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'document-viewer',
    steps: documentViewerTourSteps,
    readyToShow: !isLoading && (!!blobUrl || !!docxHtml),
    autoStartDelay: 600,
  });

  /** Format a UTC ISO string to relative or short time. */
  const formatTime = useCallback((dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
    if (isNaN(date.getTime())) return dateStr;
    const now = Date.now();
    const diff = now - date.getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }, []);

  // 1. Fetch document metadata + findings overlay (JSON)
  useEffect(() => {
    dispatch(fetchDocumentContent(sessionUuid, documentId) as any);
  }, [dispatch, sessionUuid, documentId]);

  // ── Shared DOCX-to-HTML converter with finding anchors ──
  const convertBlobToHtml = useCallback(async (blob: Blob, findingsForAnchors?: FindingOverlay[]): Promise<string | null> => {
    const ct = (blob.type || '').toLowerCase();
    const isDocx =
      ct.includes('officedocument') ||
      ct.includes('msword') ||
      documentName.toLowerCase().endsWith('.docx') ||
      documentName.toLowerCase().endsWith('.doc');

    if (!isDocx) return null;

    const arrayBuffer = await blob.arrayBuffer();
    let html = await extractHtmlFromDocx(arrayBuffer);
    if (!html || html.trim().length === 0) return null;

    // Inject anchor markers — prefer target_subsection, fall back to word-based matching
    if (findingsForAnchors && findingsForAnchors.length > 0) {
      for (const f of findingsForAnchors) {
        // Strategy 1: target_subsection — match the paragraph starting with that section number
        if (f.target_subsection && !html.includes(`id="finding-${f.uuid}"`)) {
          const secNum = f.target_subsection.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Match a section number at the start of text content (after potential HTML tags)
          const secPattern = `(>\\s*${secNum}[.\\s\\t])`;
          try {
            const secRegex = new RegExp(secPattern, 'i');
            const replaced = html.replace(secRegex, `><span id="finding-${f.uuid}" class="finding-anchor">${f.target_subsection} </span>`);
            if (replaced !== html) {
              html = replaced;
              continue; // Skip word-based matching
            }
          } catch { /* Skip */ }
        }

        // Strategy 2: word-based matching (existing logic)
        if (!f.original_text) continue;
        const words = f.original_text.trim().split(/\s+/).slice(0, 8);
        if (words.length < 2) continue;
        const wordPatterns = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        // Allow HTML tags + whitespace between words so <b>word</b> still matches
        const pattern = wordPatterns.join('(?:<[^>]*>|\\s)+');
        try {
          const regex = new RegExp(`(${pattern})`, 'i');
          html = html.replace(regex, `<span id="finding-${f.uuid}" class="finding-anchor">$1</span>`);
        } catch {
          // Skip findings whose text produces an invalid regex
        }
      }
    }

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; line-height: 1.6; color: #1f2937; background: #ffffff; }
  table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  td, th { border: 1px solid #d1d5db; padding: 8px; }
  img { max-width: 100%; height: auto; }
  del { color: #dc2626; text-decoration: line-through; background: #fef2f2; }
  ins, u { color: #16a34a; text-decoration: underline; background: #f0fdf4; }
  .finding-anchor { scroll-margin-top: 20px; }
  .finding-anchor.highlight { background: #fef3c7; border-radius: 2px; transition: background 0.6s ease; }
  .finding-anchor.highlight-fade { background: transparent; transition: background 1.5s ease; }
  html::-webkit-scrollbar { width: 8px; }
  html::-webkit-scrollbar-thumb { background: rgba(5,9,21,0.7); border-radius: 4px; }
</style>
<script>
  function findSectionInDom(sectionRef) {
    if (!sectionRef) return null;
    var ref = sectionRef.replace(/\\s+/g, ' ').trim().toLowerCase();
    // Extract the section number (e.g. "5.4" from "Section 5.4" or "5.4 Servicenivåviten")
    var numMatch = ref.match(/(\\d+(?:\\.\\d+)+)/);
    var sectionNum = numMatch ? numMatch[1] : null;
    var blocks = document.querySelectorAll('p, li, td, th, h1, h2, h3, h4, h5, h6, blockquote, dt, dd');
    // Strategy 1: Find a block that starts with the section number (e.g. "5.4 ...")
    if (sectionNum) {
      for (var i = 0; i < blocks.length; i++) {
        var bt = blocks[i].textContent.replace(/\\s+/g, ' ').trim();
        if (bt.length > 0 && (bt.indexOf(sectionNum + ' ') === 0 || bt.indexOf(sectionNum + '.') === 0 || bt.indexOf(sectionNum + '\\t') === 0 || bt === sectionNum)) {
          blocks[i].classList.add('finding-anchor');
          return blocks[i];
        }
      }
    }
    // Strategy 2: Try matching the full section reference text
    for (var i = 0; i < blocks.length; i++) {
      var bt = blocks[i].textContent.replace(/\\s+/g, ' ').trim().toLowerCase();
      if (bt.length > 0 && bt.indexOf(ref) !== -1) {
        blocks[i].classList.add('finding-anchor');
        return blocks[i];
      }
    }
    // Strategy 3: Try matching just the section number anywhere in the text start (first 20 chars)
    if (sectionNum) {
      for (var i = 0; i < blocks.length; i++) {
        var bt = blocks[i].textContent.replace(/\\s+/g, ' ').trim();
        var head = bt.substring(0, 20);
        if (head.indexOf(sectionNum) !== -1) {
          blocks[i].classList.add('finding-anchor');
          return blocks[i];
        }
      }
    }
    return null;
  }
  function findTextInDom(text) {
    var norm = text.replace(/\\s+/g, ' ').trim().toLowerCase();
    var blocks = document.querySelectorAll('p, li, td, th, h1, h2, h3, h4, h5, h6, blockquote, dt, dd');
    var lengths = [120, 60, 30];
    for (var li = 0; li < lengths.length; li++) {
      var frag = norm.substring(0, Math.min(norm.length, lengths[li]));
      if (frag.length < 8) continue;
      for (var i = 0; i < blocks.length; i++) {
        var bt = blocks[i].textContent.replace(/\\s+/g, ' ').trim().toLowerCase();
        if (bt.length > 0 && bt.indexOf(frag) !== -1) {
          blocks[i].classList.add('finding-anchor');
          return blocks[i];
        }
      }
    }
    return null;
  }
  function highlightEl(el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('highlight');
    setTimeout(function() {
      el.classList.remove('highlight');
      el.classList.add('highlight-fade');
      setTimeout(function() { el.classList.remove('highlight-fade'); }, 1500);
    }, 1500);
  }
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'scrollToFinding') {
      var el = document.getElementById('finding-' + e.data.findingUuid);
      if (!el && e.data.targetSubsection) {
        el = findSectionInDom(e.data.targetSubsection);
      }
      if (!el && e.data.sectionReference) {
        el = findSectionInDom(e.data.sectionReference);
      }
      if (!el && e.data.searchText) {
        el = findTextInDom(e.data.searchText);
      }
      if (el) highlightEl(el);
    }
  });
</script>
</head><body>${html}</body></html>`;
  }, [documentName]);

  // 2. Stream the original document binary
  useEffect(() => {
    let cancelled = false;

    const streamDoc = async () => {
      setIsStreaming(true);
      setStreamError(false);
      const result = await dispatch(
        streamReviewDocument(sessionUuid, documentId) as any,
      );
      if (cancelled) return;

      if (result?.success && result.blob) {
        const ct = (result.contentType || '').toLowerCase();
        setStreamedContentType(ct);

        try {
          const html = await convertBlobToHtml(result.blob, findings);
          if (html) {
            setDocxHtml(html);
          } else {
            setBlobUrl(URL.createObjectURL(result.blob));
          }
        } catch (err) {
          console.error('DOCX conversion failed, falling back to download:', err);
          setBlobUrl(URL.createObjectURL(result.blob));
        }
      } else {
        setStreamError(true);
      }
      setIsStreaming(false);
    };

    streamDoc();
    return () => { cancelled = true; };
  }, [dispatch, sessionUuid, documentId, convertBlobToHtml]);

  // 3. Stream the revised document when in revised mode
  useEffect(() => {
    if (viewSource !== 'revised') return;
    if (lastFetchedStyle.current === revisionStyle) return; // already fetched this style

    let cancelled = false;

    const streamRevised = async () => {
      setIsStreamingRevised(true);
      setRevisedError(false);
      setNoRevisionAvailable(false);

      // Clean up previous revised blob URL
      if (revisedBlobUrl) {
        URL.revokeObjectURL(revisedBlobUrl);
        setRevisedBlobUrl(null);
      }
      setRevisedDocxHtml(null);

      const result = await dispatch(
        streamRevisedDocument(sessionUuid, documentId, revisionStyle) as any,
      );
      if (cancelled) return;

      if (result?.noRevision) {
        setNoRevisionAvailable(true);
        setIsStreamingRevised(false);
        return;
      }

      if (result?.success && result.blob) {
        setRevisedContentType(result.contentType || '');

        try {
          const html = await convertBlobToHtml(result.blob, findings);
          if (html) {
            setRevisedDocxHtml(html);
          } else {
            setRevisedBlobUrl(URL.createObjectURL(result.blob));
          }
        } catch (err) {
          console.error('Revised DOCX conversion failed:', err);
          setRevisedBlobUrl(URL.createObjectURL(result.blob));
        }
        lastFetchedStyle.current = revisionStyle;
      } else {
        setRevisedError(true);
      }
      setIsStreamingRevised(false);
    };

    streamRevised();
    return () => { cancelled = true; };
  }, [dispatch, sessionUuid, documentId, viewSource, revisionStyle, convertBlobToHtml]);

  // Reset revision cache when switching back to original
  useEffect(() => {
    if (viewSource === 'original') {
      lastFetchedStyle.current = null;
    }
  }, [viewSource]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      if (revisedBlobUrl) URL.revokeObjectURL(revisedBlobUrl);
    };
  }, [blobUrl, revisedBlobUrl]);

  // ── Active content for rendering (original vs revised) ──
  const activeBlobUrl = viewSource === 'revised' ? revisedBlobUrl : blobUrl;
  const activeDocxHtml = viewSource === 'revised' ? revisedDocxHtml : docxHtml;
  const activeContentType = viewSource === 'revised' ? revisedContentType : streamedContentType;
  const activeLoading = viewSource === 'revised' ? isStreamingRevised : isStreaming;
  const activeError = viewSource === 'revised' ? revisedError : streamError;

  // Determine rendering strategy from content-type / file_type
  const viewerType = useMemo(() => {
    if (activeDocxHtml) return 'docx' as const;
    if (!activeBlobUrl) return null;

    const fileType = (documentContent?.file_type || '').toLowerCase();
    const ct = activeContentType.toLowerCase();

    const isPdf = fileType.includes('pdf') || ct.includes('pdf');
    const isImage =
      fileType.includes('png') || fileType.includes('jpg') || fileType.includes('jpeg') ||
      ct.includes('image/');
    const isDocx =
      fileType.includes('doc') || fileType.includes('word') || fileType === 'docx' ||
      ct.includes('officedocument') || ct.includes('msword');
    const isText = fileType === 'txt' || fileType === 'csv' || ct.includes('text/');

    if (isPdf) return 'pdf' as const;
    if (isImage) return 'image' as const;
    if (isText) return 'text' as const;
    if (isDocx) return 'download' as const;
    return 'download' as const;
  }, [activeBlobUrl, activeDocxHtml, documentContent?.file_type, activeContentType]);

  const handleDownload = useCallback(() => {
    const url = activeBlobUrl || blobUrl;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    const suffix = viewSource === 'revised' ? `_${revisionStyle}` : '';
    const base = documentName.replace(/(\.[^.]+)$/, `${suffix}$1`);
    a.download = base;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [activeBlobUrl, blobUrl, documentName, viewSource, revisionStyle]);

  // Scroll-to-finding: post message to iframe (with section reference + text fallback)
  const scrollToFinding = useCallback((findingUuid: string, searchText?: string, sectionReference?: string, targetSubsection?: string) => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: 'scrollToFinding', findingUuid, searchText: searchText || '', sectionReference: sectionReference || '', targetSubsection: targetSubsection || '' },
        '*',
      );
    }
  }, []);

  const findings: FindingOverlay[] = documentContent?.findings_overlay || [];
  const loading = isLoading || activeLoading;

  // Check if document type supports revision (DOCX/PDF only)
  const supportsRevision = useMemo(() => {
    const ft = (documentContent?.file_type || '').toLowerCase();
    return ['docx', 'doc', 'pdf'].includes(ft);
  }, [documentContent?.file_type]);

  return (
    <div className="flex flex-col w-full h-full">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 pb-3 border-b border-gray-100 dark:border-gray-700/60" data-tour-id="doc-viewer-toolbar">
        {/* Left: metadata + source toggle */}
        <div className="flex items-center gap-4">
          {documentContent && (
            <span className="text-xs text-gray-400">
              {documentContent.file_type?.toUpperCase()}
              {documentContent.page_count ? ` · ${documentContent.page_count} pages` : ''}
            </span>
          )}

          {/* Original / Revised toggle — subtle pill */}
          {supportsRevision && (
            <div className="flex items-center rounded-full bg-gray-100 dark:bg-gray-800 p-0.5">
              {(['original', 'revised'] as ViewSource[]).map((src) => (
                <button
                  key={src}
                  onClick={() => setViewSource(src)}
                  className={clsx(
                    'px-3 py-1 text-xs font-medium rounded-full transition-all duration-200',
                    viewSource === src
                      ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
                  )}
                >
                  {src === 'original' ? t('Original') : t('Revised')}
                </button>
              ))}
            </div>
          )}

          {/* Clean / Redline sub-toggle — subtle pill */}
          {supportsRevision && viewSource === 'revised' && (
            <div className="flex items-center rounded-full bg-gray-100 dark:bg-gray-800 p-0.5">
              {(['clean', 'redline'] as RevisionStyle[]).map((style) => (
                <button
                  key={style}
                  onClick={() => {
                    setRevisionStyle(style);
                    lastFetchedStyle.current = null; // force re-fetch
                  }}
                  className={clsx(
                    'px-3 py-1 text-xs font-medium rounded-full transition-all duration-200',
                    revisionStyle === style
                      ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
                  )}
                >
                  {style === 'clean' ? t('Clean') : t('Redline')}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: findings toggle + download */}
        <div className="flex items-center gap-2">
          {findings.length > 0 && (
            <button
              onClick={() => setShowFindings(!showFindings)}
              className={clsx(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                showFindings
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
              )}
            >
              {t('Findings')} ({findings.length})
            </button>
          )}
          {(activeBlobUrl || blobUrl) && (
            <button
              onClick={handleDownload}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={t('Download')}
            >
              <PiDownloadSimpleLight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Document body */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Document viewer */}
        <div className="flex-1 min-w-0 flex flex-col bg-white dark:bg-gray-900" data-tour-id="doc-viewer-content">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <LogoSpinner size="medium" />
            </div>
          ) : viewSource === 'revised' && noRevisionAvailable ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
              <PiArrowsClockwiseLight className="w-14 h-14" />
              <p className="text-sm font-medium">{t('No Revised Version Yet')}</p>
              <p className="text-xs text-gray-400 max-w-sm text-center">
                {t('Accept or modify findings to generate a revised document with changes applied.')}
              </p>
            </div>
          ) : activeError || (!activeBlobUrl && !activeDocxHtml) ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <PiFileTextLight className="w-16 h-16 mb-4" />
              <p className="text-sm">{t('Unable to load document')}</p>
            </div>
          ) : viewerType === 'docx' && activeDocxHtml ? (
            <div className="flex-1 flex justify-center bg-white dark:bg-gray-900 overflow-auto">
              <iframe
                ref={iframeRef}
                title={documentName}
                srcDoc={activeDocxHtml}
                className="border-0 w-[860px] h-full bg-white"
              />
            </div>
          ) : viewerType === 'pdf' ? (
            <iframe
              src={activeBlobUrl as string}
              className="w-full flex-1 border-0"
              title={documentName}
            />
          ) : viewerType === 'image' ? (
            <div className="flex items-center justify-center h-full p-8 overflow-auto">
              <img
                src={activeBlobUrl as string}
                alt={documentName}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : viewerType === 'text' ? (
            <iframe
              src={activeBlobUrl as string}
              className="w-full flex-1 border-0"
              title={documentName}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-4">
              <PiFileTextLight className="w-16 h-16" />
              <p className="text-sm">{t('This file type cannot be previewed directly.')}</p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                <PiDownloadSimpleLight className="w-4 h-4 inline mr-1.5" />
                {t('Download Document')}
              </button>
            </div>
          )}
        </div>

        {/* Findings sidebar */}
        {showFindings && findings.length > 0 && (
          <div className="w-80 flex-shrink-0 border-l border-gray-100 dark:border-gray-700/60 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900" data-tour-id="doc-viewer-findings">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/60">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('Findings')} ({findings.length})
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar-main">
              {findings.map((f, idx) => {
                const di = f.decision ? decisionIcons[f.decision] : null;
                return (
                  <button
                    key={f.uuid}
                    onClick={() => scrollToFinding(f.uuid, f.original_text || f.issue_description || '', f.section_reference || '', f.target_subsection || '')}
                    className="w-full text-left px-4 py-3 border-b border-gray-50 dark:border-gray-800/60 hover:bg-gray-50/60 dark:hover:bg-gray-800/30 transition-colors group"
                  >
                    {/* Top row: severity dot + clause type + decision status */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className={clsx(
                        'w-2 h-2 rounded-full flex-shrink-0',
                        severityDotColor[f.severity] || 'bg-gray-400',
                      )} />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-1 flex-1">
                        {formatLabel(f.clause_type)}
                      </span>
                      {f.action_type === 'add_after' && (
                        <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded flex-shrink-0">
                          Add
                        </span>
                      )}
                      {di && (
                        <span className={clsx('text-xs font-medium flex-shrink-0', di.color)}>
                          {di.label}
                        </span>
                      )}
                    </div>

                    {/* Section reference */}
                    {f.section_reference && (
                      <p className="text-[11px] text-gray-400 pl-4 mb-1">
                        {f.section_reference}
                      </p>
                    )}

                    {/* Issue description */}
                    {f.issue_description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed pl-4">
                        {f.issue_description}
                      </p>
                    )}

                    {/* Decision metadata: who and when */}
                    {f.decision && f.decision_by_name && (
                      <div className="flex items-center gap-1.5 mt-1.5 pl-4">
                        <span className="text-[11px] text-gray-400">
                          {f.decision_by_name}
                        </span>
                        {f.decision_at && (
                          <span className="text-[11px] text-gray-300 dark:text-gray-600">
                            · {formatTime(f.decision_at)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Subtle scroll hint on hover */}
                    <div className="text-[10px] text-gray-300 dark:text-gray-600 mt-1.5 pl-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      {t('Click to scroll to section')}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <FeatureTour
        steps={documentViewerTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default DocumentViewerModal;
